import { useEffect, useState } from 'react';
import graphQLClient from '../../graphql-client';
import { GET_PRODUCT_RECOMMENDATIONS, GET_PRODUCT_SELECTIONS } from '../../graphql/querys/querys';
import { getProductSelectionClientId } from '../../utils/productSelectionStorage';
import type {
  IProductsCard,
  ProductRecommendationsResponse,
  RecommendationSelectionsResponse
} from '../interfaces';

const mergeRecommendationSelections = async (products: IProductsCard[]) => {
  const productIds = products.map((product) => product.id).filter(Boolean);
  if (!productIds.length) return products;

  try {
    const response = await graphQLClient.request<RecommendationSelectionsResponse>(
      GET_PRODUCT_SELECTIONS,
      {
        productIds,
        clientId: getProductSelectionClientId(),
      }
    );
    const selectionsByProductId = new Map(
      response.getProductSelectionStates
        .filter((selection) => selection.productId && selection.selectedMaterial)
        .map((selection) => [String(selection.productId), String(selection.selectedMaterial)])
    );

    return products.map((product) => ({
      ...product,
      selectedMaterial: selectionsByProductId.get(product.id) || product.selectedMaterial,
    }));
  } catch {
    return products;
  }
};

export const useProductRecommendations = (productId?: string) => {
  const [products, setProducts] = useState<IProductsCard[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(productId));

  useEffect(() => {
    if (!productId) {
      setProducts([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    graphQLClient.request<ProductRecommendationsResponse>(GET_PRODUCT_RECOMMENDATIONS, { productId })
      .then(({ productRecommendations }) => mergeRecommendationSelections(productRecommendations))
      .then((productRecommendations) => {
        if (!cancelled) {
          setProducts(productRecommendations);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  return { products, isLoading };
};
